/*
 * r16pm (2026-10-24), DESIGN D:/NecroWork/dm-chats-evidence/DESIGN.md 6.6 / 6.10 / 8.5 / 8.7 and owner decision O8: the pure side of
 * the "Chat data" page (pages/ChatDataPage.js) - the account's retention picker and the slot-data erase. No React, no network.
 *
 * The server's shapes (NSB-r16p b96b38d, chats/ui/ChatsUiController.java):
 *   GET / PUT /uiapi/chats/settings     {retentionDays, retentionChoices:[7, 30, 90], aiDailyCap, dailySendCap, hourlySendCap,
 *                                        repliesDisabled, editable:["retentionDays"]}; PUT body {retentionDays}; 422 retention_invalid
 *   DELETE /uiapi/chats/slots/{slot}/data
 *                                       {erased:true, rows, settingsReset:true, readsAgainIf771Ticked:true}; 404 slot_not_found
 * Both need the server's chats switch ON (404 chats_disabled otherwise). O8: 30 days is the default (the server's own default).
 */

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** O8's default and the choices the design names; used only when the server sent no choices of its own. */
export const DEFAULT_RETENTION_DAYS = 30;
export const FALLBACK_RETENTION_CHOICES = [7, 30, 90];

/** GET /uiapi/chats/settings -> the page's model. Missing numbers stay null (drawn as "not reported"), never invented. */
export function normaliseChatSettings(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const choicesRaw = Array.isArray(r.retentionChoices) ? r.retentionChoices : null;
  const choices = choicesRaw
    ? Array.from(new Set(choicesRaw.map(num).filter((d) => d !== null && d > 0 && Number.isInteger(d)))).sort((a, b) => a - b)
    : [];
  const editable = Array.isArray(r.editable) ? r.editable.filter((x) => typeof x === 'string') : null;
  return {
    retentionDays: num(r.retentionDays),
    retentionChoices: choices.length ? choices : FALLBACK_RETENTION_CHOICES,
    choicesFromServer: choices.length > 0,
    aiDailyCap: num(r.aiDailyCap),
    dailySendCap: num(r.dailySendCap),
    hourlySendCap: num(r.hourlySendCap),
    repliesDisabled: r.repliesDisabled === true,
    // the server lists what the customer may change; an older answer without the list = retention editable (the route exists)
    retentionEditable: editable === null ? true : editable.includes('retentionDays'),
  };
}

/** The words of one choice. */
export function retentionLabel(days, current = null) {
  const base = `${days} days`;
  const tags = [];
  if (days === DEFAULT_RETENTION_DAYS) tags.push('recommended');
  if (current !== null && days === current) tags.push('current');
  return tags.length ? `${base} (${tags.join(', ')})` : base;
}

/** A change to a SHORTER period erases text older than it at the next clean-up - the page asks first. */
export function isShortening(from, to) {
  return Number.isFinite(from) && Number.isFinite(to) && to < from;
}

/** The typed confirmation of the erase: "erase <slot name>". */
export function erasePhrase(slot) {
  return `erase ${slot || ''}`.trim();
}

/** True only when the typed text is exactly the phrase (spaces around and between words are forgiven, case is not for the slot). */
export function eraseConfirmed(typed, slot) {
  if (!slot || typeof typed !== 'string') return false;
  const t = typed.trim().replace(/\s+/g, ' ');
  const m = /^erase (.+)$/i.exec(t);
  return !!m && m[1] === slot;
}

/** A slot NAME the page may put in a URL and a path (slot801, the Slots page's names): never a handle. */
export function slotNameFrom(v) {
  return typeof v === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(v) ? v : null;
}

/** DELETE answer -> the result line (server words; no number invented). */
export function eraseResultText(answer, slot) {
  const a = answer && typeof answer === 'object' ? answer : {};
  const rows = num(a.rows);
  const parts = [`${slot}'s chat data is erased${rows !== null ? ` (${rows.toLocaleString('en-US')} stored rows deleted)` : ''}.`];
  if (a.settingsReset === true) parts.push(`Its "DM Inbox (Chats)" settings are back to their defaults, so "Read my DM inbox" (771) is unticked.`);
  return parts.join(' ');
}

/** Said plainly (review of the server, design 6.6): the erase does not stop a later import. */
export const REIMPORT_TEXT = 'Ticking "Read my DM inbox (Chats)" (771) again re-imports: at the slot\'s next session the worker reads its inbox '
  + 'again and stores the conversations that are on the phone then. Leave 771 unticked to keep this slot\'s chats off the server.';
